import {
  ScreenPosition,
  TElement,
  TLeerraum,
  TEvaluateFunctions,
  TTeil,
  TVerteilungsstrategie,
} from "./assistantApi";
import { saveUserFeedback } from "../../commons/Utils";
import { proxy, unproxy } from "./proxies";
import { I18N } from "oc-I18N";
import * as Commons from "oc-commons/Commons";
import * as Maths2 from "oc-threejs/Maths2";
import * as Maths3 from "oc-threejs/Maths3";
import { Aufbau } from "../../arrangement/Aufbau";
import { changeStreckenLaenge } from "../../arrangement/grundrissFunctions";
import { roundValues } from "oc-threejs/Debugs";
import { v2, v3 } from "oc-threejs/short3";
import { resetGrundrissCamera } from "../../pages/Grundriss";
import { Vector2, Vector3 } from "three";
import { Segment } from "../../arrangement/Segment";
import { sanitizeString, createRateLimiter, createUIInteractionHelpers, sleep, captureScreen } from "btg-ai-agent";
import { ScreenUtils } from "oc-threejs/ScreenUtils";
import { getCurrentUIState as genericGetCurrentUIState } from "btg-ai-agent";
import { findPathIntersectionCached } from "oc-app/metadata/GraphMetadata";
import { elementOrTor } from "../../arrangement/elementFunctions";
import { PAuswahlwert } from "../../../metamodell_paths";

// ── 3D Scene State & Screen Coordinates ───────────────────────────────────────

export function screenPosition(p) {
  const screenUtils = new ScreenUtils();
  return roundValues(screenUtils.worldToScreenCoordinates(p).toArray(), 0);
}

export function getCurrent3dSceneState() {
  return {
    selectedObjectIds: APP.arrangement.selectedObjects.map((part) => part.id),
    segmente: APP.arrangement.segmente.map((segment) => ({
      ecken: segment.ecken.map((e) => ({
        position3d: roundValues(e.position.toJSON(), 2),
        positionScreen: screenPosition(e.position),
        pfostenId: e.pfosten?.id,
      })),
      direction3d: roundValues(segment.direction.toJSON(), 2),
      directionScreen: screenPosition(segment.direction),
      teile: segment.getPartsAndPfosten().map((part) => proxy(part).toJSON()),
    })),
  };
}

// ── Security & Rate Limiting ──────────────────────────────────────────────────

const feedbackRateLimit = createRateLimiter("submitFeedback", 5);

const uiHelpers = createUIInteractionHelpers({
  highlightClass: "highlighted-for-10-seconds",
  highlightDurationMs: 10_000,
  rateLimitMaxCalls: 60,
});

// ── getCurrentUIState: App-specific wrapper ───────────────────────────────────

export function getCurrentUIState({
  withPositionDetails = false,
}: { withPositionDetails?: boolean } = {}) {
  return genericGetCurrentUIState({
    withPositionDetails,
    precision: 2,
    getAppContext: () => ({
      page: APP.pageName,
      userLoggedIn: APP.settings.userLoggedIn,
      userName: APP.settings.userName,
      currency: APP.settings.currency,
      tenant: APP.settings.tenant_key,
      lang: APP.settings.lang,
      siteUrl: APP.settings.siteUrl,
      warnungen: _.uniqBy(
        APP.arrangement.warnungen.map((warnung) => warnung.toJSON()),
        (w) => w.text
      ),
    }),
  });
}

export type AddOptionCallback = (option: string, callback: () => void) => void;

export const EvaluateFunctions: TEvaluateFunctions = {
  getBestellliste() {
    return {
      bestellliste: APP.arrangement.toJSON().bestellliste,
      gesamtpreis: APP.arrangement.toJSON().gesamtpreis,
    };
  },

  getBaugruppen() {
    return APP.arrangement.toJSON().baugruppen;
  },

  async navigateToPage(pageName: string) {
    if (!APP.pageSequence.includes(pageName)) {
      return {
        success: false,
        reason: "page does not exist, did you mean: " + APP.pageSequence.join(),
      };
    }
    this.highlightUIElement(pageName);
    await APP.showPage(pageName);
    // Kurz warten, damit React die neue Page vollständig gerendert hat
    await sleep(100);
    return {
      success: true,
    };
  },

  submitFeedback(message: string) {
    feedbackRateLimit();
    const sanitized = sanitizeString(message, 2000);
    if (!sanitized.trim()) {
      throw new Error("Feedback-Nachricht darf nicht leer sein.");
    }
    saveUserFeedback(sanitized, null);
  },

  showTutorial() {
    window["___addButtonCallback"](I18N("Tutorial anzeigen"), () =>
      (APP.pageInstance as any).showMouseTutorial()
    );
  },

  undo() {
    APP.undoHandler.undo();
  },

  redo() {
    APP.undoHandler.redo();
  },

  resetCamera() {
    if (APP.pageName === "Grundriss") {
      resetGrundrissCamera();
    } else if (APP.pageName === "Zauneditor" || APP.pageName === "Masseditor") {
      APP.zoomCameraToPart(null);
    } else {
      APP.resetCamera();
    }
  },

  // getProductDB,

  getCurrentUIState,

  getWarnungen() {
    return APP.arrangement.toJSON().warnungen;
  },

  getUserActionLog(lastLines: number = 20) {
    return APP.userActionLog.slice(-lastLines).map((log) => {
      const l = { ...log } as any;
      return l;
    });
  },

  async highlightUIElement(name: string) {
    return uiHelpers.highlightUIElement(name);
  },

  async clickUIElement(name: string) {
    return uiHelpers.clickUIElement(name);
  },

  async setUISelectValue(selectName: string, optionValue: string) {
    return uiHelpers.setUISelectValue(selectName, optionValue);
  },

  async setUIInputValue(inputName: string, value: string | number) {
    return uiHelpers.setUIInputValue(inputName, value);
  },

  getCurrent3dSceneState,

  getAllTeile(): TTeil[] {
    return APP.arrangement
      .getPartsAndPfosten()
      .filter((p) => p != null)
      .sort((p) => screenPosition(p.getWorldPosition()))
      .map(proxy) as TTeil[];
  },

  findClosestTeilToScreenPosition(teile: TTeil[], screenPosition: ScreenPosition): TTeil {
    if (!teile || teile.length === 0) return undefined;
    const distance = (v) =>
      Math.hypot(v.screenPosition[0] - screenPosition[0], v.screenPosition[1] - screenPosition[1]);
    return teile.length < 2
      ? teile[0]
      : teile.reduce((a, b) => (distance(a) < distance(b) ? a : b));
  },

  findClosestTeilToWorldPosition(teile: any[], point: { x: number; y: number }) {
    const parts = teile.map((t) => unproxy(t));
    const point_ = v2(point.x, point.y);
    return proxy(
      _.minBy(parts, (p) => {
        const polygon: Vector2[] = p
          .getOBBWorld()
          .getCorners()
          .map((c: Vector3) => Maths3.v3To2xz(c));
        if (Maths2.isPointInPolygon(point_, polygon)) return 0;
        return _.min(
          Commons.pairwise(polygon).map((edge) => Maths2.distanceToLineSegment(point_, edge))
        );
      })
    );
  },

  findClosestAufbauToWorldPosition(aufbauten: any[], point: { x: number; y: number }) {
    return this.findClosestTeilToWorldPosition(aufbauten, point);
  },

  getTeilById(id: number): TTeil {
    return proxy(
      APP.arrangement.getPartsAndPfosten().find((p) => p.id == (id as any)) ??
        APP.arrangement.segmente.find((s) => s.id == (id as any))
    );
  },

  removeTeile(teile: TTeil[]) {
    toArray(teile)
      .map(unproxy)
      .filter((p) => !p.isPfosten)
      .forEach((p) => p.uiRemove(p));
  },

  async setSelectedTeile(teile: TTeil[]) {
    await APP.arrangement.setSelectedObjects(toArray(teile).map(unproxy));
  },

  getSelectedTeile(): TTeil[] {
    return APP.arrangement.selectedObjects.map(proxy);
  },

  applyGenericStyle(source: TTeil, target: TTeil) {
    unproxy(target).copy(unproxy(source));
  },

  updateTeilProperty(teilId: number, propertyName: string, value: any) {
    const part = APP.arrangement.getPartsAndPfosten().find((p) => p.id == (teilId as any));
    if (part) {
      const proxied = proxy(part);
      if (Object.keys(proxied).includes(propertyName)) {
        proxied[propertyName] = value;
        updateAfterChange();
      } else {
        console.warn(`Property ${propertyName} does not exist on part ${teilId}`);
      }
    }
  },

  swapTeile(teilA: TTeil, teilB: TTeil) {
    APP.arrangement.tauscheBenachbarteElemente([teilA, teilB].map(unproxy));
  },

  fillLeerraum(leerraum: TLeerraum, element?: TElement) {
    const elementSchablone = APP.arrangement.createDefaultElementWithout3D();
    proxy(elementSchablone).set(element as any);
    unproxy(leerraum).fillWithElements(elementSchablone);
  },

  findClosestSegment(point: { x: number; y: number }) {
    return proxy(findClosestSegmentToPoints([point]));
  },

  getAvailableFuellungen() {
    const serie = APP.arrangement.serie;
    const füllungen = APP.metadata.Auswahlwert.filter((a) => {
      return (
        APP.metadata.Element?.some(
          (e) =>
            e.Füllung?.some((f) => f.Auswahlwert === a) && e.DetailSerie.some((s) => s === serie)
        ) ||
        APP.metadata.Tor?.some(
          (t) =>
            t.Füllung?.some((f) => f.Auswahlwert === a) && t.DetailSerie.some((s) => s === serie)
        )
      );
    })
      .filter((a) => !a.Gesperrt)
      .map((a) => a.Key);
    return füllungen;
  },

  getDetailSerieForFuellung(füllungsKey) {
    const füllungAuswahlwert = APP.metadata.Auswahlwert.find(
      (a) => a.Auswahl?.Key === "Füllung" && a.Key === füllungsKey
    );
    if (!füllungAuswahlwert) return undefined;

    const detailSerie = findPathIntersectionCached("assistantImpl:getDetailSerieForFuellung")(
      füllungAuswahlwert,
      (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).DetailSerie
    );

    return detailSerie?.Auswahlwert?.Key;
  },

  sleep(ms) {
    return sleep(ms);
  },

  captureScreen() {
    return captureScreen();
  },

  // ── Globale Zaun-Einstellungen ──────────────────────────────────────────

  getZaunDefaults() {
    const a = APP.arrangement;
    return {
      inspiration: a.inspiration?.Inspiration?.Key,
      detailSerie: a.inspiration?.DetailSerie?.Auswahlwert?.Key,
      serie: a.serie?.Key,
      füllung: a.füllungAuswahlwert?.Key,
      pfostenfarbe: a.pfostenfarbeAuswahlwert_?.Key,
      pfostenart: a.pfostenartAuswahlwert?.Key,
      bodenbefestigung: a.bodenbefestigungAuswahlwert_?.Key,
      elementhalter: a.elementhalterAuswahlwert_?.Key,
      verteilungsstrategie: a.verteilungsstrategieAuswahlwert_?.Key as TVerteilungsstrategie,
      profil: a.profilAuswahlwert_?.Key,
      profilleiste: a.profilleisteAuswahlwert_?.Key,
      abschlussleiste: a.abschlussleisteAuswahlwert_?.Key,
    };
  },

  setZaunDefaults(params) {
    const a = APP.arrangement;
    const find = (key: string) => APP.richMetadata.findEnsureAuswahlwert(key);
    if (params.inspiration != null) {
      const insp = APP.richMetadata.findInspiration(params.inspiration);
      if (!insp) throw new Error(`Inspiration "${params.inspiration}" nicht gefunden.`);
      a.inspiration = insp;
    }
    if (params.füllung != null) a.füllungAuswahlwert = find(params.füllung);
    if (params.pfostenfarbe != null) a.pfostenfarbeAuswahlwert_ = find(params.pfostenfarbe);
    if (params.pfostenart != null) a.pfostenartAuswahlwert = find(params.pfostenart);
    if (params.bodenbefestigung != null)
      a.bodenbefestigungAuswahlwert_ = find(params.bodenbefestigung);
    if (params.elementhalter != null) a.elementhalterAuswahlwert_ = find(params.elementhalter);
    if (params.verteilungsstrategie != null)
      a.verteilungsstrategieAuswahlwert_ = find(params.verteilungsstrategie);
    if (params.profil != null) a.profilAuswahlwert_ = find(params.profil);
    if (params.profilleiste != null) a.profilleisteAuswahlwert_ = find(params.profilleiste);
    if (params.abschlussleiste != null)
      a.abschlussleisteAuswahlwert_ = find(params.abschlussleiste);
    updateAfterGrundrissChange();
  },

  // ── Grundriss ───────────────────────────────────────────────────────────

  getGrundriss() {
    const g = APP.arrangement.grundriss;
    const segmente = APP.arrangement.segmente;

    const data = {
      strecken: g.getStrecken().map((strecke, index) => ({
        index,
        von: { x: strecke[0].x, y: strecke[0].y },
        bis: { x: strecke[1].x, y: strecke[1].y },
      })),
      ecken: g.ecken.map((e) => ({
        id: e.id,
        punkt: { x: e.punkt.x, y: e.punkt.y },
      })),
      kanten: g.kanten.map((k, i) => ({
        index: i,
        id: k.id,
        eckenIds: k.ecken.map((e) => e.id),
        laenge: k.ecken[0].punkt.distanceTo(k.ecken[1].punkt),
        segmenteIds: segmente.filter((s) => s.kanteId == k.id).map((s) => s.id),
      })),
      segmente: segmente.map(proxy),
    };
    return data;
  },

  resetGrundrissToForm(form, seitenLängen?) {
    APP.arrangement.grundriss.setStandardForm(form, seitenLängen);
    updateAfterGrundrissChange();
  },

  clearGrundriss() {
    APP.arrangement.grundriss.setStandardForm("Leer");
    updateAfterGrundrissChange();
  },

  addGrundrissStrecke(von, bis) {
    const existingStrecken = getExistingStrecken();
    existingStrecken.push([v2(von.x, von.y), v2(bis.x, bis.y)]);
    APP.arrangement.grundriss.setStrecken(existingStrecken as any);
    updateAfterGrundrissChange();
  },

  addGrundrissPolylinie(punkte) {
    if (!punkte || punkte.length < 2) {
      throw new Error("addGrundrissPolylinie benötigt mindestens 2 Punkte.");
    }
    const existingStrecken = getExistingStrecken();
    for (let i = 0; i < punkte.length - 1; i++) {
      existingStrecken.push([v2(punkte[i].x, punkte[i].y), v2(punkte[i + 1].x, punkte[i + 1].y)]);
    }
    APP.arrangement.grundriss.setStrecken(existingStrecken as any);
    updateAfterGrundrissChange();
  },

  removeGrundrissStrecke(streckeIndex) {
    const strecken = getExistingStrecken();
    if (streckeIndex < 0 || streckeIndex >= strecken.length) {
      throw new Error(
        `Strecke mit Index ${streckeIndex} nicht gefunden. Es gibt ${strecken.length} Strecken (0-${strecken.length - 1}).`
      );
    }
    strecken.splice(streckeIndex, 1);
    if (strecken.length === 0) {
      APP.arrangement.grundriss.setStandardForm("Leer");
    } else {
      APP.arrangement.grundriss.setStrecken(strecken as any);
    }
    updateAfterGrundrissChange();
  },

  setGrundrissKantenLaenge(kanteIndex, neueLaenge) {
    const kanten = APP.arrangement.grundriss.kanten;
    if (kanteIndex < 0 || kanteIndex >= kanten.length) {
      throw new Error(
        `Kante mit Index ${kanteIndex} nicht gefunden. Es gibt ${kanten.length} Kanten (0-${kanten.length - 1}).`
      );
    }
    const kante = kanten[kanteIndex];
    changeStreckenLaenge(kanten, new Map(), kante.id, neueLaenge);
    updateAfterGrundrissChange();
  },

  // ── Aufbauten ───────────────────────────────────────────────────────────

  getAufbauten() {
    return APP.arrangement.aufbauten.map(serializeAufbau);
  },

  getAvailableAufbauTypen() {
    const typen = APP.metadata.Aufbauten.map((a) => ({ Key: a.Key, Label: a.Label }));
    return typen;
  },

  addAufbauFromCenterAndSize(typ, center, size) {
    const aufbauMeta = findAufbauMetaOrThrow(typ);
    const newAufbau = new Aufbau(APP.arrangement, {
      Key: Commons.createUniqueIdString(),
      aufbauMeta,
      position: v3(center.x, 0, center.y),
      size: v3(size.x, aufbauMeta.Size3d.y, size.y),
      drehung: 0,
      fixedSize: aufbauMeta.fixedSize,
    });
    APP.arrangement.aufbauten.push(newAufbau);
    updateAfterGrundrissChange();
    return serializeAufbau(newAufbau);
  },

  addAufbauAnStrecke(typ, start, ende, breite, seite) {
    const aufbauMeta = findAufbauMetaOrThrow(typ);

    const dx = ende.x - start.x;
    const dy = ende.y - start.y;
    const laenge = Math.hypot(dx, dy);
    if (laenge < 0.001) throw new Error("start und ende sind (nahezu) identisch.");

    // Einheits-Normalenvektor senkrecht zur Strecke
    // "links" = linke Seite wenn man von start nach ende blickt (Draufsicht: x→rechts, y→fern)
    const sign = seite === "links" ? 1 : -1;
    const nx = (-dy / laenge) * sign;
    const ny = (dx / laenge) * sign;

    // Center = Mittelpunkt der Strecke + halbe Breite in Normalenrichtung
    const center = v3(
      (start.x + ende.x) / 2 + (nx * breite) / 2,
      0,
      (start.y + ende.y) / 2 + (ny * breite) / 2
    );

    // Drehung: lokale X-Achse soll entlang start→ende zeigen
    // setFromAxisAngle(Y, θ): lokales (1,0,0) → Welt (cosθ, 0, -sinθ)  →  θ = atan2(-dy, dx)
    const drehung = Math.atan2(-dy, dx);

    const newAufbau = new Aufbau(APP.arrangement, {
      Key: Commons.createUniqueIdString(),
      aufbauMeta,
      position: center,
      size: v3(laenge, aufbauMeta.Size3d.y, breite),
      drehung,
      fixedSize: aufbauMeta.fixedSize,
    });
    APP.arrangement.aufbauten.push(newAufbau);
    updateAfterGrundrissChange();
    return serializeAufbau(newAufbau);
  },

  removeAufbau(aufbauKey) {
    const aufbau = APP.arrangement.aufbauten.find((a) => a.Key === aufbauKey);
    if (!aufbau) {
      throw new Error(`Aufbau mit Key "${aufbauKey}" nicht gefunden.`);
    }
    APP.arrangement.uiRemoveAufbauten([aufbau]);
    APP.arrangement.aufbauten = APP.arrangement.aufbauten.filter((a) => a !== aufbau);
    updateAfterGrundrissChange();
  },

  setAufbauPosition(aufbauKey, position) {
    const aufbau = APP.arrangement.aufbauten.find((a) => a.Key === aufbauKey);
    if (!aufbau) {
      throw new Error(`Aufbau mit Key "${aufbauKey}" nicht gefunden.`);
    }
    aufbau.position.set(position.x, 0, position.y);
    updateAfterGrundrissChange();
  },

  setAufbauSize(aufbauKey, size) {
    const aufbau = APP.arrangement.aufbauten.find((a) => a.Key === aufbauKey);
    if (!aufbau) {
      throw new Error(`Aufbau mit Key "${aufbauKey}" nicht gefunden.`);
    }
    const minMax = aufbau.getMassMinMaxBox3();
    aufbau.size.setX(Math.max(size.x, minMax.min.x));
    aufbau.size.setZ(Math.max(size.y, minMax.min.z));
    updateAfterGrundrissChange();
  },

  setAufbauRotation(aufbauKey, winkelGrad) {
    const aufbau = APP.arrangement.aufbauten.find((a) => a.Key === aufbauKey);
    if (!aufbau) {
      throw new Error(`Aufbau mit Key "${aufbauKey}" nicht gefunden.`);
    }
    aufbau.drehung = ((360 - (winkelGrad % 360)) * Math.PI) / 180;
    updateAfterGrundrissChange();
  },

  // ── Tore ────────────────────────────────────────────────────────────────

  addTor(form, start, ende, öffnungsrichtung, anschlagsrichtung = "start") {
    const streckeV2 = [v2(start.x, start.y), v2(ende.x, ende.y)];

    // Prüfe, ob ein existierendes Segment beide Punkte (start und ende) enthält
    const EPSILON = 0.05;
    const existingSegment = APP.arrangement.segmente.find((seg) => {
      const punkte = seg.segmentKante?.punkte;
      if (!punkte || punkte.length < 2) return false;
      return (
        Maths2.distanceToLineSegment(streckeV2[0], punkte) < EPSILON &&
        Maths2.distanceToLineSegment(streckeV2[1], punkte) < EPSILON
      );
    });

    let segment: Segment;

    if (existingSegment) {
      // Existierendes Segment nutzen – keine neue Strecke nötig
      segment = existingSegment;
    } else {
      // Neue Grundriss-Strecke hinzufügen.
      // Dadurch können Segmente entstehen oder verschmelzen.
      const existingStrecken = getExistingStrecken();
      existingStrecken.push(streckeV2);
      APP.arrangement.grundriss.setStrecken(existingStrecken as any);
      APP.arrangement.update3d();

      // Das passende Segment finden
      segment = unproxy(
        this.findClosestSegment({
          x: (start.x + ende.x) / 2,
          y: (start.y + ende.y) / 2,
        })
      );
      if (!segment) {
        throw new Error("Konnte kein Segment für die Strecke von start nach ende finden.");
      }
    }

    const breite = streckeV2[0].distanceTo(streckeV2[1]);

    // X-Intervall des Tors auf dem Segment berechnen
    const streckeV3 = streckeV2.map((v) => Maths3.v2To3xz(v));
    const xs = streckeV3.map((p) => segment.getXOfWorldPosition(p));
    const xMiddle = (xs[0] + xs[1]) / 2;

    // "normale" Richtung = xs wachsen (Ecke 0 → Ecke 1)
    const normal = xs[0] < xs[1];
    return placeTorOnSegment(
      segment,
      form,
      xs[0],
      xs[1],
      xMiddle,
      breite,
      normal,
      öffnungsrichtung,
      anschlagsrichtung
    );
  },

  addTorOnSegment(segmentId, form, startX, breite, öffnungsrichtung, anschlagsrichtung = "start") {
    const segment = APP.arrangement.segmente.find((s) => s.id === segmentId);
    if (!segment) {
      const ids = APP.arrangement.segmente.map((s) => s.id).join(", ");
      throw new Error(`Segment mit id ${segmentId} nicht gefunden. Vorhandene IDs: ${ids}`);
    }

    const x0 = startX;
    const x1 = startX + breite;
    const xMiddle = (x0 + x1) / 2;

    return placeTorOnSegment(
      segment,
      form,
      x0,
      x1,
      xMiddle,
      breite,
      true,
      öffnungsrichtung,
      anschlagsrichtung
    );
  },
};

/** Gemeinsamer Update-Lifecycle nach jeder Grundriss-Mutation */
function updateAfterGrundrissChange() {
  updateAfterChange();
}

function updateAfterChange() {
  APP.arrangement.update3d();
  APP.dirty();
  APP.undoHandler.save();
}

/** Extracts existing line segments (Strecke2[]) from the Grundriss graph structure.
 *  Each kante becomes one [von, bis] pair – matching the format expected by setStrecken / streckenZuGraph. */
function getExistingStrecken(): any[][] {
  const g = APP.arrangement.grundriss;
  return (g.kanten ?? []).map((kante) => [kante.ecken[0].punkt.clone(), kante.ecken[1].punkt.clone()]);
}

function serializeAufbau(a: Aufbau) {
  return {
    key: a.Key,
    typ: a.aufbauMeta?.Key,
    label: a.aufbauMeta?.Label,
    position: { x: a.position.x, y: a.position.z },
    size: { x: a.size.x, y: a.size.z },
    drehung: (360 - (a.drehung * 180) / Math.PI) % 360,
  };
}

function toArray(parts) {
  return parts == null ? null : Array.isArray(parts) ? parts : [parts];
}

function findAufbauMetaOrThrow(typ: string) {
  const aufbauMeta = APP.metadata.Aufbauten.find((a) => a.Key === typ || a.Label === typ);
  if (!aufbauMeta) {
    const available = APP.metadata.Aufbauten.map((a) => `${a.Key} (${a.Label})`).join(", ");
    throw new Error(`Aufbautyp "${typ}" nicht gefunden. Verfügbar: ${available}`);
  }
  return aufbauMeta;
}

function findClosestSegmentToPoints(pointsXY: { x: number; y: number }[]) {
  const points = pointsXY.map(({ x, y }) => v2(x, y));
  return _.minBy(APP.arrangement.segmente, (s) =>
    _.sum(
      points.map((p) =>
        Maths2.distanceToLineSegment(
          p,
          s.ecken.map((e) => Maths3.v3To2xz(e.position))
        )
      )
    )
  );
}

window["ä"] = EvaluateFunctions;

function placeTorOnSegment(
  segment: Segment,
  form: string,
  x0: number,
  x1: number,
  xMiddle: number,
  breite: number,
  normal: boolean,
  öffnungsrichtung: string,
  anschlagsrichtung: string
) {
  // Bestehende Elemente entfernen, deren X-Intervall vom Tor geschnitten wird
  const overlapping = segment.filterPartsBetweenPositionsX(x0, x1);
  overlapping.forEach((part) => segment.remove(part));

  const tor = APP.arrangement.createElementFromJson({
    füllungAuswahlwert: APP.arrangement.füllungAuswahlwert.Key,
    massAuswahlwert: form + "_Easy",
    eAntriebAuswahlwert: "E_Antrieb_ohne",
    breite,
  });
  tor.snap = 0;

  // Tor auf dem Segment platzieren
  segment.addPart(tor);
  segment.setX(tor, 0.5, xMiddle);

  const normaleÖffnungsRichtung = normal === (öffnungsrichtung === "links");
  const normaleAnschlagsRichtung = normal === (anschlagsrichtung === "ende");

  if (form === "Schiebetor") {
    tor.schiebetormotorseiteAuswahlwert = APP.richMetadata.findAuswahlwert(
      normaleÖffnungsRichtung ? "Schiebetormotorseite_A" : "Schiebetormotorseite_B"
    );
    tor.schieberichtungAuswahlwert = APP.richMetadata.findAuswahlwert(
      normaleAnschlagsRichtung ? "Schieberichtung_rechts" : "Schieberichtung_links"
    );
  } else if (form == "Doppeltor") {
    tor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert(
      normaleÖffnungsRichtung ? "Öffnung_nach_innen" : "Öffnung_nach_außen"
    );
    tor.gehflügelAuswahlwert = APP.richMetadata.findAuswahlwert(
      normaleÖffnungsRichtung == normaleAnschlagsRichtung ? "Gehflügel_rechts" : "Gehflügel_links"
    );
  } else if (form == "Einzeltor") {
    tor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert(
      normaleÖffnungsRichtung ? "Öffnung_nach_innen" : "Öffnung_nach_außen"
    );
    tor.anschlagAuswahlwert = APP.richMetadata.findAuswahlwert(
      normaleÖffnungsRichtung == normaleAnschlagsRichtung ? "Anschlag_links" : "Anschlag_rechts"
    );
  } else {
    throw new Error("unknown form " + form);
  }

  updateAfterGrundrissChange();

  return proxy(tor);
}
